import { EventSubscriber } from '@verdant-web/common';
import { Metadata } from '../metadata/Metadata.js';
import { openMetadataDatabase } from '../metadata/openMetadataDatabase.js';
import { openDocumentDatabase } from '../openDocumentDatabase.js';
import { UndoHistory } from '../UndoHistory.js';
import { Client } from './Client.js';
/**
 * Since storage initialization is async, this class wraps the core
 * Storage creation promise and exposes some metadata which can
 * be useful immediately.
 */
export class ClientDescriptor {
    get namespace() {
        return this._namespace;
    }
    constructor(init) {
        this.init = init;
        this._initializing = false;
        this.initialize = async (init) => {
            // if server-side and no alternative IndexedDB implementation was provided,
            // we can't initialize the storage
            if (!('indexedDB' in window) && !init.indexedDb) {
                throw new Error('A verdant client was initialized in an environment without IndexedDB. If you are using verdant in a server-rendered framework, you must enforce that all clients are initialized on the client-side, or you must provide some mock interface of IDBFactory to the ClientDescriptor options.');
            }
            if (this._initializing || this._resolvedValue) {
                return this._readyPromise;
            }
            this._initializing = true;
            try {
                const metaDbName = [init.namespace, 'meta'].join('_');
                const { db: metaDb } = await openMetadataDatabase(this._namespace, {
                    indexedDB: init.indexedDb,
                    log: init.log,
                    databaseName: metaDbName,
                });
                const context = {
                    namespace: this._namespace,
                    metaDb,
                    schema: init.schema,
                    log: init.log || (() => { }),
                    undoHistory: init.undoHistory || new UndoHistory(),
                    entityEvents: new EventSubscriber(),
                };
                const meta = new Metadata({
                    context,
                    disableRebasing: init.disableRebasing,
                });
                // verify schema integrity
                await meta.updateSchema(init.schema, init.overrideSchemaConflict);
                const documentDb = await openDocumentDatabase({
                    context,
                    version: init.schema.version,
                    meta,
                    migrations: init.migrations,
                    indexedDB: init.indexedDb,
                });
                const fullContext = Object.assign(context, { documentDb });
                const storage = new Client({
                    syncConfig: init.sync,
                    migrations: init.migrations,
                    files: init.files,
                }, fullContext, {
                    meta,
                });
                this.resolveReady(storage);
                this._resolvedValue = storage;
                return storage;
            }
            catch (err) {
                this.rejectReady(err);
                throw err;
            }
            finally {
                this._initializing = false;
            }
        };
        this.open = () => this.initialize(this.init);
        this.close = async () => {
            if (this._resolvedValue) {
                this._resolvedValue.close();
            }
            if (this._initializing) {
                (await this._readyPromise).close();
            }
        };
        this._readyPromise = new Promise((resolve, reject) => {
            this.resolveReady = resolve;
            this.rejectReady = reject;
        });
        this._namespace = init.namespace;
    }
    get current() {
        // exposing an immediate value if already resolved lets us
        // skip the promise microtask when accessing this externally if
        // the initialization has been completed.
        return this._resolvedValue;
    }
    get readyPromise() {
        return this._readyPromise;
    }
    get schema() {
        return this.init.schema;
    }
}
//# sourceMappingURL=ClientDescriptor.js.map